import { supabase } from '@/integrations/supabase/client';

interface GoogleMapsKeysResult {
  mapsApiKey: string;
  placesApiKey: string;
  source: 'primary' | 'fallback';
}

export interface MapsKeyHealth {
  primaryHealthy: boolean;
  primaryReason?: string;
  primaryConfigured: boolean;
  fallbackHealthy: boolean;
  fallbackReason?: string;
  fallbackConfigured: boolean;
  activeSource: 'primary' | 'fallback' | null;
  testedAt: string | null;
  lastFetchedAt: string | null;
}

// In-memory cache for the API keys
let cachedMapsKey: string | null = null;
let cachedPlacesKey: string | null = null;
let cachedSource: 'primary' | 'fallback' | null = null;

// Exported health cache for admin dashboard
export let lastKeyHealth: MapsKeyHealth = {
  primaryHealthy: false,
  primaryConfigured: false,
  fallbackHealthy: false,
  fallbackConfigured: false,
  activeSource: null,
  testedAt: null,
  lastFetchedAt: null,
};

/**
 * Clears the in-memory key cache so the next call re-fetches from the edge function.
 * Useful for the admin "Test Now" button.
 */
export const clearMapsKeyCache = () => {
  cachedMapsKey = null;
  cachedPlacesKey = null;
  cachedSource = null;
};

/**
 * Gets both Google Maps API keys with primary/fallback strategy via Supabase edge function.
 * The edge function handles key validation and fallback logic server-side.
 */
export const getMapsKeys = async (forceRefresh = false): Promise<GoogleMapsKeysResult> => {
  // Return cached keys if available (unless forced refresh)
  if (!forceRefresh && cachedMapsKey && cachedPlacesKey && cachedSource) {
    console.debug('Using cached Google Maps API keys (source:', cachedSource, ')');
    return {
      mapsApiKey: cachedMapsKey,
      placesApiKey: cachedPlacesKey,
      source: cachedSource,
    };
  }

  try {
    console.debug('Fetching Google Maps API keys from Supabase edge function...');

    const fetchWithTimeout = Promise.race([
      supabase.functions.invoke('get-maps-key'),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Supabase key fetch timeout after 8s')), 8000)
      ),
    ]);

    const { data, error } = await fetchWithTimeout;

    if (error) throw new Error(error.message);

    // Always update health cache from response, even on allFailed
    if (data) {
      lastKeyHealth = {
        primaryHealthy: data.primaryHealthy ?? false,
        primaryReason: data.primaryReason,
        primaryConfigured: data.primaryConfigured ?? false,
        fallbackHealthy: data.fallbackHealthy ?? false,
        fallbackReason: data.fallbackReason,
        fallbackConfigured: data.fallbackConfigured ?? false,
        activeSource: data.source ?? null,
        testedAt: data.testedAt ?? new Date().toISOString(),
        lastFetchedAt: new Date().toISOString(),
      };
    }

    // If both keys failed, the edge function returns allFailed: true with null keys
    if (data?.allFailed) {
      const reason = data.primaryReason || data.fallbackReason || 'All API keys failed validation';
      console.error('Google Maps API keys: all failed.', reason);
      throw new Error(`Google Maps API keys unavailable: ${reason}`);
    }

    if (data?.mapsApiKey && data?.placesApiKey) {
      cachedMapsKey = data.mapsApiKey;
      cachedPlacesKey = data.placesApiKey;
      cachedSource = data.source ?? 'primary';

      console.debug('Google Maps API keys fetched. Active source:', cachedSource);
      return {
        mapsApiKey: cachedMapsKey,
        placesApiKey: cachedPlacesKey,
        source: cachedSource,
      };
    }

    throw new Error(data?.error || 'No keys returned from edge function');
  } catch (err) {
    console.error('Failed to fetch Google Maps API keys:', err);
    // Only reset activeSource; keep the health data already populated above
    lastKeyHealth = {
      ...lastKeyHealth,
      activeSource: null,
      lastFetchedAt: new Date().toISOString(),
    };
    throw err;
  }
};

/**
 * Backward compatibility: Gets only the Maps API key
 */
export const getGoogleMapsApiKey = async (): Promise<{ apiKey: string; source: 'primary' | 'fallback' }> => {
  const keys = await getMapsKeys();
  return { apiKey: keys.mapsApiKey, source: keys.source };
};

/**
 * Checks if Google Maps JavaScript API is already loaded
 */
export const isGoogleMapsLoaded = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    typeof window.google !== 'undefined' &&
    typeof window.google.maps !== 'undefined'
  );
};

/**
 * Gets the status of Google Maps loading
 */
export const getGoogleMapsStatus = () => {
  if (typeof window === 'undefined') return 'server-side';
  if (isGoogleMapsLoaded()) return 'loaded';
  const scripts = document.getElementsByTagName('script');
  for (let i = 0; i < scripts.length; i++) {
    if (scripts[i].src.includes('maps.googleapis.com')) return 'loading';
  }
  return 'not-loaded';
};
